import { requiredAdminPermissionsForPath } from "services/adminAccess";
import {
  currentAdminCountryScope,
  setCurrentAdminCountryScope,
} from "helpers/storage";
import useAdminAccess from "hooks/useAdminAccess";
import { useEffect, useState } from "react";
import { Link, useLocation } from "react-router-dom";
import { adminRoutes } from "routes/routes";
import { logoutAdmin } from "utilities/network";

const countryScopes = [
  { label: "All", value: "all" },
  { label: "Nigeria", value: "NG" },
  { label: "Ghana", value: "GH" },
  { label: "Kenya", value: "KE" },
];

const AdminSidebar: React.FC = () => {
  const [menuOpen, setMenuOpen] = useState(false);
  const [countryScope, setCountryScope] = useState(currentAdminCountryScope());
  const { session } = useAdminAccess();
  const assignedCountryScope = session?.countryScope;
  const availableCountryScopes =
    assignedCountryScope === "all" || !assignedCountryScope
      ? countryScopes
      : countryScopes.filter(({ value }) => value === assignedCountryScope);
  const selectedCountryScope =
    assignedCountryScope && assignedCountryScope !== "all"
      ? assignedCountryScope
      : countryScope;

  useEffect(() => {
    if (
      assignedCountryScope &&
      assignedCountryScope !== "all" &&
      countryScope !== assignedCountryScope
    ) {
      setCountryScope(assignedCountryScope);
      setCurrentAdminCountryScope(assignedCountryScope);
    }
  }, [assignedCountryScope, countryScope]);

  const handleCountryScopeChange = (
    event: React.ChangeEvent<HTMLSelectElement>,
  ) => {
    const nextCountryScope = event.target.value;
    setCountryScope(nextCountryScope);
    setCurrentAdminCountryScope(nextCountryScope);
    window.location.reload();
  };

  return (
    <div className="relative px-5 py-5 bg-black md:py-10 md:w-1/6">
      <div className="flex items-center justify-between gap-4">
        <h1>
          <Link to={adminRoutes.dashboard}>
            <img
              className="object-contain w-[130px] pl-4"
              alt="Bitoshi.africa logo"
              src="/images/logo-white.png"
            />
          </Link>
        </h1>
        <button
          type="button"
          className="px-3 py-2 text-sm text-white border border-gray-700 rounded md:hidden"
          aria-expanded={menuOpen}
          aria-controls="admin-navigation"
          onClick={() => setMenuOpen(!menuOpen)}
        >
          {menuOpen ? "Close menu" : "Menu"}
        </button>
      </div>

      <div className={`px-4 mt-7 ${menuOpen ? "" : "hidden md:block"}`}>
        <label
          className="flex items-center gap-2 mb-2 text-[11px] font-medium text-gray-500"
          htmlFor="admin-country-scope"
        >
          <i className="text-[10px] fa fa-globe"></i>
          Market scope
        </label>
        <div className="relative">
          <i className="pointer-events-none absolute left-3 top-1/2 -translate-y-1/2 text-[11px] text-gray-500 fa fa-location-dot"></i>
          <select
            id="admin-country-scope"
            aria-label="Admin country scope"
            disabled={Boolean(
              assignedCountryScope && assignedCountryScope !== "all",
            )}
            className="w-full h-10 py-0 text-sm font-medium text-gray-100 transition border border-gray-800 rounded-md outline-none appearance-none cursor-pointer pl-9 pr-9 bg-gray-950 hover:border-gray-700 focus:border-orange-300 focus:ring-2 focus:ring-orange-300/20"
            value={selectedCountryScope}
            onChange={handleCountryScopeChange}
          >
            {availableCountryScopes.map(({ label, value }) => (
              <option key={value} value={value}>
                {label}
              </option>
            ))}
          </select>
        </div>
      </div>

      <nav
        id="admin-navigation"
        className={`mt-10 ${menuOpen ? "" : "hidden md:block"}`}
      >
        <ul className="text-left">
          <NavLink
            to={adminRoutes.dashboard}
            title="Dashboard"
            icon="fa-home"
          />
          <NavLink
            to="#"
            title="Transactions"
            isDrop
            icon="fa-file-lines"
            sub={[
              {
                to: `${adminRoutes.transactions}/naira`,
                title: "Fiat",
              },
              {
                to: `${adminRoutes.transactions}/market`,
                title: "Buy & Sell",
              },
              {
                to: `${adminRoutes.transactions}/send-receive`,
                title: "Send & Receive",
              },
              {
                to: `${adminRoutes.transactions}/swap`,
                title: "Swap",
              },
              {
                to: `${adminRoutes.transactions}/recurring`,
                title: "Recurring Buy",
              },
              {
                to: `${adminRoutes.transactions}/vouchers`,
                title: "Vouchers",
              },
              {
                to: `${adminRoutes.transactions}/admin-market`,
                title: "Admin Market",
              },
              {
                to: `${adminRoutes.transactions}/missed-transaction`,
                title: "Restore Missed Transaction",
              },
              {
                to: `${adminRoutes.transactions}/risk-control`,
                title: "Risk Control",
              },
              {
                to: adminRoutes.withdrawalReviews,
                title: "Withdrawal Reviews",
              },
              {
                to: adminRoutes.flaggedTransactions,
                title: "Flagged Transactions",
              },
            ]}
          />
          <NavLink
            to={adminRoutes.referrals}
            title="Refer & Earn"
            icon="fa-gift"
          />
          <NavLink to={adminRoutes.users} title="Users" icon="fa-users" />
          <NavLink
            to="#"
            title="Gamification"
            isDrop
            icon="fa-trophy"
            sub={[
              {
                to: `${adminRoutes.gamification}/overview`,
                title: "Overview",
              },
              {
                to: `${adminRoutes.gamification}/progression`,
                title: "ELO & Tiers",
              },
              {
                to: `${adminRoutes.gamification}/actions`,
                title: "Actions",
              },
              {
                to: `${adminRoutes.gamification}/streaks`,
                title: "Streak & Milestones",
              },
              {
                to: `${adminRoutes.gamification}/rewards`,
                title: "Rewards",
              },
              {
                to: `${adminRoutes.gamification}/fulfilment`,
                title: "Pending Rewards",
              },
              {
                to: `${adminRoutes.gamification}/history`,
                title: "Change History",
              },
            ]}
          />
          <NavLink
            to={adminRoutes.virtualCards}
            title="Virtual Cards"
            icon="fa-credit-card"
          />
          <NavLink
            to={adminRoutes.assistant}
            title="AI Assistant"
            icon="fa-comments"
          />
          <NavLink
            to={adminRoutes.devices}
            title="Devices"
            icon="fa-mobile-screen"
          />
          <NavLink
            to="#"
            title="KYC"
            icon="fa-house-circle-check"
            isDrop
            sub={[
              {
                to: adminRoutes.addressVerificationReviews,
                title: "Identity & Address",
              },
              { to: adminRoutes.eddReviews, title: "Tier 3 · EDD" },
              { to: adminRoutes.kybReviews, title: "Tier 4 · KYB" },
            ]}
          />
          <NavLink
            to={adminRoutes.merchants}
            title="Merchants"
            icon="fa-user-group"
          />
          <NavLink
            to="#"
            title="Analytics"
            isDrop
            icon="fa-chart-line"
            sub={[
              {
                to: `${adminRoutes.analytics}/transactions`,
                title: "Transactions",
              },
              {
                to: `${adminRoutes.analytics}/revenue`,
                title: "Revenue",
              },
              {
                to: `${adminRoutes.analytics}/acquisitions`,
                title: "Acquisitions",
              },
              {
                to: `${adminRoutes.analytics}/growth-metrics`,
                title: "Growth Metrics",
              },
            ]}
          />
          <NavLink to={adminRoutes.rates} title="Rates" icon="fa-percent" />
          <NavLink to={adminRoutes.cashback} title="Cashback" icon="fa-coins" />
          <NavLink
            to={adminRoutes.bitoshiHour}
            title="Bitoshi Hour"
            icon="fa-regular fa-calendar"
          />
          <NavLink
            to={adminRoutes.otcTransactions}
            title="OTC Transactions"
            icon="fa-exchange"
          />
          <NavLink
            to="#"
            title="Deposits"
            isDrop
            icon="fa-money-bill-transfer"
            sub={[
              {
                to: `${adminRoutes.deposits}/pending`,
                title: "Pending",
              },
              {
                to: `${adminRoutes.deposits}/completed`,
                title: "Completed",
              },
            ]}
          />
          <NavLink
            to={adminRoutes.settlements}
            title="Settlements"
            icon="fa-scale-balanced"
          />
          <NavLink
            to={adminRoutes.creditUser}
            title="Credit User"
            icon="fa-dollar"
          />
          <NavLink
            to={adminRoutes.testSms}
            title="Test SMS Verification"
            icon="fa-envelope"
          />
          <NavLink
            to={adminRoutes.notifyAll}
            title="Notify Users"
            icon="fa-users"
          />
          <NavLink
            to={adminRoutes.paymentMerchant}
            title="Payment Merchant"
            icon="fa-dollar"
          />
          <NavLink
            to={adminRoutes.paymentOperations}
            title="Payment Operations"
            icon="fa-wallet"
          />
          <NavLink
            to={adminRoutes.btcBalance}
            title="Bitcoin Balance"
            icon="fa-bitcoin"
          />
          <NavLink
            to={adminRoutes.approvals}
            title="Approval Queue"
            icon="fa-list-check"
          />
          <NavLink
            to={`${adminRoutes.accessControl}/roles`}
            title="Access Control"
            icon="fa-user-shield"
          />
        </ul>

        <div
          className={`pl-4 text-left rounded-lg py-2 bg-gray-900 text-white mt-10
          w-44`}
        >
          <p className="block w-full cursor-pointer" onClick={logoutAdmin}>
            <i className={`fa fa-arrow-right-from-bracket`}></i>
            <span className="ml-4">Logout</span>
          </p>
        </div>
      </nav>
    </div>
  );
};

const NavLink: React.FC<{
  to: string;
  title: string;
  icon: string;
  isDrop?: boolean;
  sub?: Array<{ to: string; title: string }>;
}> = ({ to, title, icon, isDrop, sub }) => {
  const { accessReady, canAny } = useAdminAccess();
  const location = useLocation();
  const isAllowed = (path: string) =>
    canAny(requiredAdminPermissionsForPath(path) ?? []);
  const visibleSub = sub?.filter((item) => isAllowed(item.to));
  const isSubActive = Boolean(
    visibleSub?.some(({ to: subRoute }) =>
      location.pathname.startsWith(subRoute),
    ),
  );
  const [isOpen, setIsOpen] = useState(isSubActive);
  const isActive = isDrop
    ? isSubActive
    : to === "/"
      ? location.pathname === "/"
      : location.pathname.includes(to.slice(1));

  if (!accessReady || (!isDrop && !isAllowed(to))) {
    return null;
  }
  if (isDrop && !visibleSub?.length) {
    return null;
  }

  return (
    <li
      className={`pl-4 rounded-lg py-2 mb-5 ${
        isActive ? "bg-gray-700 text-white" : "text-gray-400"
      }`}
    >
      {isDrop ? (
        <a
          href="#side"
          className="block w-full cursor-pointer"
          onClick={(event) => {
            event.preventDefault();
            setIsOpen((p) => !p);
          }}
        >
          <i className={`fa ${icon}`}></i>
          <span className="ml-4">{title}</span>
          {isDrop && (
            <i className={`fa fa-chevron-${isOpen ? "up" : "down"} ml-3`}></i>
          )}
        </a>
      ) : (
        <Link className="block w-full" to={to}>
          <i className={`fa ${icon}`}></i>
          <span className="ml-4">{title}</span>
          {isDrop && <i className="ml-3 fa fa-chevron-down"></i>}
        </Link>
      )}
      {isDrop && isOpen ? (
        <ul className="ml-1 text-left border-l border-l-gray-600">
          {visibleSub?.map(({ title, to }, index) => (
            <li
              key={String(index)}
              className={`ml-4 pl-2 rounded-lg py-2 mt-3 ${
                isActive ? "bg-gray-700 text-white" : "text-gray-400"
              }`}
            >
              <Link className="block w-full" to={to}>
                <span className="ml-4">{title}</span>
              </Link>
            </li>
          ))}
        </ul>
      ) : null}
    </li>
  );
};

export default AdminSidebar;
